import { ReactNode, useMemo, useEffect, useRef } from "react";
import { TourKey, useTourContext, Tours } from "./TourProvider";
import { get } from "lodash";
import { Page, TourConditionType } from "./TourProvider.types";

/*
 * Hook to get the tour step component and its anchor ID.
 * @param tour - The tour key
 * @param stepNumber - Which tour step to render
 * @param additionalAction - An optional additional action to execute when the next button is pressed
 */
export const useTourStep = (
  tour: TourKey,
  stepNumber: number,
  additionalAction?: () => void,
) => {
  const getTourStepComponentRenderer = useTourContext(
    (s) => s.getTourStepComponentRenderer,
  );
  const currentStep = useTourContext((s) => s.currentStep);
  const currentTour = useTourContext((s) => s.currentTour);

  const tourComponentRenderer = useMemo(
    () => getTourStepComponentRenderer(tour, stepNumber),
    [getTourStepComponentRenderer, stepNumber, tour],
  );

  // Use a ref to store the last computed component and its dependencies
  const last = useRef<{
    currentStep: number | null;
    currentTour: TourKey | null;
    additionalAction?: () => void;
    component: ReactNode;
  }>();

  let shouldUpdate = true;
  if (last.current) {
    shouldUpdate =
      last.current.currentStep !== currentStep ||
      last.current.currentTour !== currentTour ||
      last.current.additionalAction !== additionalAction;
  }

  if (shouldUpdate) {
    last.current = {
      currentStep,
      currentTour,
      additionalAction,
      component: tourComponentRenderer(
        currentStep,
        currentTour,
        additionalAction,
      ),
    };
  }

  const tourStepAnchorId = get(Tours, `${tour}.steps.${stepNumber}.anchorId`);

  return { component: last.current?.component, anchorId: tourStepAnchorId };
};

/*
 * Hook for pages to use to set the current page in the tour context.
 */
export const useSetCurrentPage = (page: Page) => {
  const setCurrentPage = useTourContext((s) => s.setCurrentPage);

  useEffect(() => {
    setCurrentPage(page);
    return () => {
      setCurrentPage(null);
    };
  }, [page, setCurrentPage]);
};

/*
 * Hook for pages to set any relevant tour conditions they might have information about.
 */
export const useSetTourCondition = (
  condition: TourConditionType,
  value: boolean,
) => {
  const setTourCondition = useTourContext((s) => s.setTourCondition);

  useEffect(() => {
    setTourCondition(condition, value);
    return () => {
      setTourCondition(condition, false);
    };
  }, [condition, setTourCondition, value]);
};
